import { runWhenIdle } from '@/utils/run-when-idle';

// Defers a side effect until the page has *finished loading* and only then to the first idle moment.
//
// Why this is not just `runWhenIdle`: idle is not the same as "after the paint". `requestIdleCallback`
// fires as soon as the main thread has a free slot, which on a quiet page happens while the LCP element is
// still being laid out — so a heavy request scheduled on idle alone can still land inside the window
// Lighthouse charges against LCP. Gating on `load` first makes the deferral deterministic: `load` fires
// after every render-blocking resource of the initial document has arrived, i.e. necessarily after the
// paint Lighthouse observes.
//
// The measurement that made this necessary (home page, PageSpeed API, 8 mobile runs): the GTM container is
// 499-684 KB, the heaviest item on every page. In the 7 runs where gtm.js finished downloading *before* the
// observed paint, Lantern-simulated LCP came out at 6,3-10,9 s and the score at 55-67. In the single run
// where it finished *after*, simulated LCP was 2,9 s and the score 86 — with observed LCP between 823 and
// 2550 ms in all 8 runs, i.e. the page itself was never slow. The whole ~20-25 point spread is about which
// side of the paint the request sits on, so anything that lets the request drift back before the paint
// (dropping the `load` gate, calling the callback synchronously, "simplifying" this to a plain effect)
// gives the points straight back.
//
// `load` may already have fired by the time an effect runs — a hydration that finishes after the document
// completes, or a client-side route change — hence the `readyState === 'complete'` branch; without it the
// callback would simply never run on those loads.
//
// Idle scheduling and its bounded fallback for browsers without `requestIdleCallback` (Safari before 17)
// are `runWhenIdle`'s job, reused as-is so there is one place where those delays are tuned. Returns a
// cancel function for effect cleanup that is safe to call in either phase (waiting for `load`, or waiting
// for idle).
export function runAfterLoadWhenIdle(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let cancelIdle: (() => void) | undefined;
  let isCancelled = false;

  const scheduleWhenIdle = () => {
    // The `load` listener can outlive the caller by a frame; without this guard a cleanup that ran while
    // the listener was already queued would be followed by the callback anyway.
    if (isCancelled) return;
    cancelIdle = runWhenIdle(callback);
  };

  if (document.readyState === 'complete') {
    scheduleWhenIdle();
  } else {
    window.addEventListener('load', scheduleWhenIdle, { once: true });
  }

  return () => {
    isCancelled = true;
    window.removeEventListener('load', scheduleWhenIdle);
    cancelIdle?.();
  };
}
